import { config } from './config';
import { clearSource, getSource } from './source';
import { clearAuth, getIdToken, getRefreshToken, setIdToken } from './auth';

export type Interval = '7d' | '1mo' | '3mo';

function redirectToLogin() {
  clearAuth();
  if (typeof window === 'undefined') return;
  if (window.location.pathname !== '/login') {
    window.location.replace('/login');
  }
}

// Sign-out: ask the API to revoke this browser's refresh token. Fire and
// forget (keepalive so it survives the navigation); local sign-out never
// waits on it.
export function revokeRefreshToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return;
  fetch(`${config.apiBase}/auth/logout`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
    keepalive: true,
  }).catch(() => {});
}

// Single-flight refresh. Multiple concurrent 401s share one POST /auth/refresh.
let inflightRefresh: Promise<string | null> | null = null;

async function refreshIdToken(): Promise<string | null> {
  if (inflightRefresh) return inflightRefresh;
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  inflightRefresh = (async () => {
    try {
      const res = await fetch(`${config.apiBase}/auth/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (!data?.ok || typeof data.idToken !== 'string') return null;
      setIdToken(data.idToken);
      return data.idToken as string;
    } catch {
      return null;
    } finally {
      inflightRefresh = null;
    }
  })();

  return inflightRefresh;
}

type FetchOpts = { method?: string; idToken?: string; body?: any; headers?: Record<string, string> };

async function doFetch(path: string, opts: FetchOpts, tok: string | undefined) {
  return fetch(`${config.apiBase}${path}`, {
    method: opts.method ?? 'GET',
    headers: {
      'content-type': 'application/json',
      ...(tok ? { authorization: `Bearer ${tok}` } : {}),
      ...(opts.headers ?? {})
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined
  });
}

export async function apiFetch<T>(path: string, opts: FetchOpts = {}): Promise<T> {
  // Callers pass `idToken` as a "this is an authed call" marker. Read the
  // actual current token from storage at fetch time so a refresh elsewhere
  // (e.g. an earlier 401 that triggered silent refresh) doesn't leave this
  // call sending the stale closure-captured value.
  const wantsAuth = !!opts.idToken;
  const tok = wantsAuth ? (getIdToken() ?? opts.idToken) : undefined;
  let res = await doFetch(path, opts, tok);

  if ((res.status === 401 || res.status === 403) && wantsAuth) {
    // Try a silent refresh before giving up. Only retry once per request.
    const fresh = await refreshIdToken();
    if (fresh) {
      res = await doFetch(path, opts, fresh);
    }
  }

  const text = await res.text();
  const data = text ? JSON.parse(text) : null;

  if (res.status === 401 || res.status === 403) {
    redirectToLogin();
    throw new Error('unauthorized');
  }

  if (!res.ok) {
    throw new Error(data?.error ?? `http_${res.status}`);
  }
  return data as T;
}

export type AttachmentInit = { name?: string; mime?: string; size: number; kind?: 'image' | 'video' | 'file' };
export type AttachmentUploadUrl = { id: string; key: string; url: string; mime: string; size: number; name: string | null; kind: string | null };

export async function initMessage(params: {
  idToken: string;
  recipientEmail: string;
  plaintext: string; // can be empty if attachments are present
  attachments: AttachmentInit[];
}) {
  return apiFetch<{ ok: boolean; messageId: string; uploadUrls: AttachmentUploadUrl[] }>(`/messages/init`, {
    method: 'POST',
    idToken: params.idToken,
    body: {
      recipientEmail: params.recipientEmail,
      plaintext: params.plaintext,
      attachments: params.attachments
    }
  });
}

export async function commitMessage(params: { idToken: string; messageId: string }) {
  return apiFetch<{ ok: boolean; messageId: string; status: string }>(`/messages/commit`, {
    method: 'POST',
    idToken: params.idToken,
    body: { messageId: params.messageId }
  });
}

export async function uploadToPresignedUrl(params: { url: string; file: File; mime: string }) {
  const res = await fetch(params.url, {
    method: 'PUT',
    headers: {
      'content-type': params.mime,
      'if-none-match': '*',
    },
    body: params.file
  });
  if (!res.ok) throw new Error(`upload_failed_${res.status}`);
}

export async function deleteMessage(params: { idToken: string; messageId: string }) {
  return apiFetch<{ ok: boolean }>(`/messages/${encodeURIComponent(params.messageId)}`, {
    method: 'DELETE',
    idToken: params.idToken,
  });
}

export async function deleteAccount(params: { idToken: string }) {
  return apiFetch<{ ok: boolean }>(`/account`, {
    method: 'DELETE',
    idToken: params.idToken,
  });
}

export async function listMessages(idToken: string, cursor?: string | null) {
  const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
  return apiFetch<{ ok: boolean; items: any[]; nextCursor?: string | null; total?: number }>(`/messages${qs}`, { idToken });
}

export async function getProfile(idToken: string) {
  return apiFetch<{ ok: boolean; profile: { storageUsedBytes: number; storageLimitBytes: number; email?: string | null; planInterval?: string; planActiveUntil?: number | null; creditsUsdCents?: number; trippedAt?: number | null; cancelledAt?: number | null; hasDevice?: boolean; lifetimeGrant?: 'first-100' | 'admin' | null; lifetimeWelcomedAt?: number | null } }>(`/profile`, { idToken, headers: getSource() ? { 'x-signup-source': getSource() as string } : undefined }).then(r => { clearSource(); return r; });
}

// Acks the one-time "lifetime, on us" welcome so it never shows again.
export async function ackLifetimeWelcome(idToken: string) {
  return apiFetch<{ ok: boolean; alreadyWelcomed?: boolean }>(`/profile/lifetime-welcome`, { method: 'POST', idToken });
}

export async function activatePlan(params: { idToken: string; interval: 'monthly' | 'yearly' | 'lifetime' }) {
  return apiFetch<{ ok: boolean; via?: 'credits'; error?: string; neededCents?: number; creditsCents?: number }>(`/billing/activate-plan`, {
    method: 'POST',
    idToken: params.idToken,
    body: { interval: params.interval }
  });
}

export async function cancelSubscription(params: { idToken: string }) {
  return apiFetch<{ ok: boolean; cancelledAt?: number; planActiveUntil?: number; alreadyCancelled?: boolean }>(`/billing/cancel-subscription`, {
    method: 'POST',
    idToken: params.idToken,
  });
}

// Crypto pay intent backed by NowPayments. Each intent has its own ephemeral
// pay_address; the user sends `payAmount` of `payCurrency` there. NowPayments
// IPN webhook flips status to PAID once their network detects confirmations.
export type CryptoPayIntent = {
  intentId: string;
  status: 'PENDING' | 'CONFIRMING' | 'PAID' | 'EXPIRED' | 'FAILED' | 'PARTIALLY_PAID' | string;
  createdAt: number;
  expiresAt: number;
  paidAt?: number | null;
  amountUsdCents: number;
  targetPlan?: 'monthly' | 'yearly' | 'lifetime' | null;
  payAddress: string;
  payAmount: string;       // exact crypto amount the user must send
  payCurrency: string;     // e.g. "usdcsol"
  paidAmount?: string | null;
  celebratedAt?: number | null;
};

export async function createCryptoPayIntent(params: { idToken: string; amountUsdCents: number; payCurrency?: string; targetPlan?: 'monthly' | 'yearly' | 'lifetime' | null }) {
  return apiFetch<{ ok: boolean; intent: CryptoPayIntent }>(`/billing/crypto/intent`, {
    method: 'POST',
    idToken: params.idToken,
    body: { amountUsdCents: params.amountUsdCents, payCurrency: params.payCurrency, targetPlan: params.targetPlan ?? undefined }
  });
}

export async function getCryptoPayIntent(params: { idToken: string; intentId: string }) {
  return apiFetch<{ ok: boolean; intent: CryptoPayIntent }>(`/billing/crypto/intent/${encodeURIComponent(params.intentId)}`, {
    method: 'GET',
    idToken: params.idToken,
  });
}

export async function listCryptoPayIntents(idToken: string) {
  return apiFetch<{ ok: boolean; intents: CryptoPayIntent[] }>(`/billing/crypto/intents`, {
    method: 'GET',
    idToken,
  });
}

export async function markCryptoPayIntentCelebrated(params: { idToken: string; intentId: string }) {
  return apiFetch<{ ok: boolean; alreadyCelebrated?: boolean }>(
    `/billing/crypto/intent/${encodeURIComponent(params.intentId)}/celebrate`,
    { method: 'POST', idToken: params.idToken }
  );
}

export async function getCryptoMinAmount(params: { idToken: string; currency: string }) {
  return apiFetch<{ ok: boolean; currency: string; minUsd: number | null; minNative: number | null }>(
    `/billing/crypto/min-amount?currency=${encodeURIComponent(params.currency)}`,
    { method: 'GET', idToken: params.idToken }
  );
}


export async function authOtpStart(email: string) {
  return apiFetch<{ ok: boolean; email: string; session: string }>(`/auth/otp/start`, {
    method: 'POST',
    body: { email }
  });
}

export async function authOtpVerify(params: { email: string; code: string; session: string }) {
  return apiFetch<{ ok: boolean; idToken: string; accessToken: string; refreshToken?: string | null }>(`/auth/otp/verify`, {
    method: 'POST',
    body: params
  });
}

export async function checkin(idToken: string) {
  return apiFetch(`/checkin`, { method: 'POST', idToken });
}

export async function checkinAll(idToken: string) {
  return apiFetch<{ ok: boolean; updated: number }>(`/checkin/all`, {
    method: 'POST',
    idToken,
  });
}

// On the wire (read): the API only returns phone + question — the passphrase
// hash is server-side only.
// On the wire (write): include `passphrase` to set/change it; omit it (or
// pass an empty string) to keep the existing one when editing other fields.
export type TrustedContact = {
  phone: string;
  question: string;
  passphrase?: string;
};

export type UserSettings = {
  onboarded?: boolean;
  checkinInterval?: Interval;
  graceInterval?: Interval;
  trustedContact?: TrustedContact | null;
  lastCheckinAt?: number | null;
  dueAt?: number | null;
  graceEndsAt?: number | null;
  nextActionAt?: number | null;
  awaitingConfirmationAt?: number | null;
};

export async function getSettings(idToken: string) {
  return apiFetch<{ ok: boolean; settings: UserSettings }>(`/settings`, { idToken });
}

export async function saveSettings(idToken: string, settings: UserSettings) {
  return apiFetch<{ ok: boolean }>(`/settings`, {
    method: 'PUT',
    idToken,
    body: settings,
  });
}

export async function redeemToken(token: string) {
  const url = `${config.tokenRedeemUrl}/?token=${encodeURIComponent(token)}`;
  const res = await fetch(url, { method: 'GET' });
  const text = await res.text();

  // token-redeem currently returns plain text errors (e.g. "token already used").
  // Be permissive in parsing.
  let data: any = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { ok: false, error: text };
    }
  }

  if (!res.ok) throw new Error(data?.error ?? `http_${res.status}`);
  return data as any;
}
